// Copyright ©️ 2026 eVolve MEP, LLC

import { FLAT_BLACK_FILLER, type HexTriplet, isHexTriplet } from '@evolvemep/api-ts';

import {
  BaseMarkupTool,
  type MarkupToolContext,
  type PanelConfig,
} from 'components/Autodesk/extensions/MarkupModeExtension/BaseMarkupTool';
import { PT_PER_INCH } from 'components/Autodesk/extensions/MarkupModeExtension/constants';
import {
  type StampMarkupData,
  type StampToolType,
  createStampClasses,
  type StampConfig,
} from 'components/Autodesk/extensions/MarkupModeExtension/StampMarkup';
import { isNotNil } from 'helpers/isNotNil';

type StampToolConfig = {
  /** Unique stamp type, e.g. 'checkmark'. */
  type: StampToolType;
  /** Label shown in the toolbar tooltip and property panel title, e.g. 'Checkmark'. */
  label: string;
  /** Raw SVG string to use as the toolbar button icon. */
  iconSvg: string;
  /** StampConfig forwarded to createStampClasses(). */
  stampConfig: StampConfig;
  defaults?: {
    fillColor?: HexTriplet;
    fillOpacity?: number;
    stampSize?: number;
  };
};

const INITIAL_DEFAULTS = {
  fillColor: FLAT_BLACK_FILLER,
  fillOpacity: 100,
  stampSize: 24,
};

/**
 * Generic tool for any stamp markup type.
 * Handles the properties panel, button state, and edit-mode lifecycle.
 * Stamp tools (CheckmarkTool, CrossMarkTool, QuestionMarkTool) are created by calling `new StampTool(...options)`.
 */
export class StampTool extends BaseMarkupTool {
  readonly type: StampToolType;
  private readonly label: string;
  private readonly iconSvg: string;
  private readonly stampConfig: StampConfig;

  constructor(context: MarkupToolContext, config: StampToolConfig) {
    super(context, {
      fillColor: config.defaults?.fillColor ?? INITIAL_DEFAULTS.fillColor,
      fillOpacity: config.defaults?.fillOpacity ?? INITIAL_DEFAULTS.fillOpacity,
      stampSize: config.defaults?.stampSize ?? INITIAL_DEFAULTS.stampSize,
    });
    this.type = config.type;
    this.label = config.label;
    this.iconSvg = config.iconSvg;
    this.stampConfig = config.stampConfig;
  }

  protected getPanelConfig(): PanelConfig {
    return {
      panelTitle: this.label,
      sections: [
        {
          heading: 'Fill',
          fields: [
            { property: 'stamp-size', label: 'Size' },
            { property: 'fill-color', colorLabel: 'Color', opacityLabel: 'Opacity' },
          ],
        },
      ],
    };
  }

  /** Overrides base because stamp size comes from `selection.size.y`, not from the SVG style object. */
  override updatePropertyPanelForSelection() {
    const selection = this.markupExtension.getSelection();
    if (!selection || selection.type !== this.type) {
      this.resetState();
    } else {
      const fillColorValue = selection.style['fill-color'] ?? '';
      const fillColor = isHexTriplet(fillColorValue) ? fillColorValue : this.state.fillColor;
      this.setStyleState({
        fillColor,
        fillOpacity: isNotNil(selection.style['fill-opacity'])
          ? selection.style['fill-opacity'] * 100
          : this.state.fillOpacity,
        // size.y is the stamp height in inches, convert back to pt (* 72).
        stampSize: Math.round(selection.size.y * PT_PER_INCH) || this.state.stampSize,
      });
    }
    this.applyPropertiesToPanel();
  }

  /** Overrides base because stamp size is applied via `editMode.stampSize`, not the SVG style object. */
  protected override applyStyles() {
    if (this.markupExtension.editMode.type !== this.type) return;

    const editMode = this.markupExtension.editMode;

    const stampSize = this.state.stampSize ?? this.defaults.stampSize ?? INITIAL_DEFAULTS.stampSize;
    const fillColor = this.state.fillColor ?? this.defaults.fillColor ?? INITIAL_DEFAULTS.fillColor;
    const fillOpacity = this.state.fillOpacity ?? this.defaults.fillOpacity ?? INITIAL_DEFAULTS.fillOpacity;

    editMode.stampSize = this.state.stampSize;
    editMode.resizeSelected?.(stampSize);

    this.markupExtension.editMode.setStyle({
      ...this.markupExtension.editMode.getStyle(),
      'fill-color': fillColor,
      'fill-opacity': fillOpacity / 100,
    });
  }

  protected activateEditMode() {
    const EditModeImpl = createStampClasses(this.stampConfig);
    this.markupExtension.changeEditMode(new EditModeImpl(this.markupExtension));
  }

  protected get buttonIcon() {
    return this.iconSvg;
  }

  protected get buttonTooltip() {
    return this.label;
  }

  /** Returns an edit-mode instance. Used by MarkupExtension to call createMarkup/getMarkupFromSvg during restore. */
  createEditMode(): {
    type: string;
    createMarkup: (data: StampMarkupData, addToHistory?: boolean) => void;
    getMarkupFromSvg: (g: SVGGElement) => StampMarkupData | null;
  } {
    const EditModeImpl = createStampClasses(this.stampConfig);
    return new EditModeImpl(this.markupExtension);
  }
}
